import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { ReassignmentAnalysis, ReassignmentStrategy } from '../lib/api';

export interface ReassignDialogState {
  jobId: string;
  phaseId: string;
  newStartDate: string;
  newEndDate: string;
  fromLabel?: string;
  isPhase: boolean;
  preview: ReassignmentAnalysis | null;
  previewError: string | null;
  executeError: string | null;
  result: ReassignmentAnalysis | null;
  onComplete?: () => void;
  onCancel?: () => void;
}

export interface StartRescheduleArgs {
  jobId: string;
  phaseId: string;
  newStartDate: string;
  newEndDate: string;
  fromLabel?: string;
  isPhase?: boolean;
  onComplete?: () => void;
  onCancel?: () => void;
}

export function useRescheduleFlow() {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<ReassignDialogState | null>(null);
  // A cancelled preview may finish after a different proposal has opened.
  const requestId = useRef(0);
  const applying = useRef(false);
  const renderedRequestId = requestId.current;

  const reassignMutation = useMutation({
    mutationFn: ({
      strategy,
      proposal,
    }: {
      strategy: ReassignmentStrategy;
      proposal: ReassignDialogState;
      request: number;
    }) => {
      return api.jobs.update(proposal.jobId, {
        startDate: proposal.newStartDate || undefined,
        endDate: proposal.newEndDate || null,
        dateChangeStrategy: strategy,
        phaseId: proposal.phaseId,
      });
    },
    onSuccess: (result, { proposal, request }) => {
      const jobId = proposal.jobId;
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['board'] });
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      if (jobId) queryClient.invalidateQueries({ queryKey: ['phases', jobId] });
      queryClient.invalidateQueries({ queryKey: ['staff', 'consecutive-days-range'] });
      setDialog((prev) =>
        prev && requestId.current === request
          ? { ...prev, executeError: null, result: result.reassignment ?? null }
          : prev,
      );
    },
    onError: (err, { request }) => {
      setDialog((prev) =>
        prev && requestId.current === request
          ? {
              ...prev,
              executeError: err instanceof Error ? err.message : 'Failed to reassign resources',
            }
          : prev,
      );
    },
    onSettled: () => {
      applying.current = false;
    },
  });

  async function startReschedule(args: StartRescheduleArgs) {
    if (applying.current) return;
    const request = ++requestId.current;
    setDialog({
      jobId: args.jobId,
      phaseId: args.phaseId,
      newStartDate: args.newStartDate,
      newEndDate: args.newEndDate,
      fromLabel: args.fromLabel,
      isPhase: args.isPhase ?? true,
      preview: null,
      previewError: null,
      executeError: null,
      result: null,
      onComplete: args.onComplete,
      onCancel: args.onCancel,
    });

    if (!args.phaseId) {
      setDialog((prev) =>
        prev ? { ...prev, previewError: 'No active phase to reschedule.' } : prev,
      );
      return;
    }

    try {
      const analysis = await api.jobs.reschedulePreview(args.jobId, {
        phaseId: args.phaseId,
        startDate: args.newStartDate,
        endDate: args.newEndDate || args.newStartDate,
      });
      setDialog((prev) =>
        prev && requestId.current === request ? { ...prev, preview: analysis } : prev,
      );
    } catch (err) {
      setDialog((prev) =>
        prev && requestId.current === request
          ? {
              ...prev,
              previewError: err instanceof Error ? err.message : 'Failed to analyse conflicts',
            }
          : prev,
      );
    }
  }

  function apply(strategy: ReassignmentStrategy) {
    // Guard the hook as well as the buttons. The backend's legacy Phase-1
    // fallback must never stand in for a missing selected phase here.
    if (
      !dialog?.phaseId ||
      !dialog.preview ||
      dialog.previewError ||
      dialog.result ||
      applying.current ||
      renderedRequestId !== requestId.current
    ) {
      return;
    }
    // Set synchronously: two clicks before React renders must still submit once.
    applying.current = true;
    reassignMutation.mutate({ strategy, proposal: dialog, request: requestId.current });
  }

  return {
    dialog,
    isExecuting: reassignMutation.isPending,
    startReschedule,
    onTransfer: () => apply('transfer'),
    onClear: () => apply('clear'),
    onCancel: () => {
      if (applying.current) return;
      requestId.current++;
      dialog?.onCancel?.();
      setDialog(null);
    },
    onDone: () => {
      if (applying.current) return;
      requestId.current++;
      dialog?.onComplete?.();
      setDialog(null);
    },
  };
}
